import type { StatusEsteira } from '../api';
import type { IEmpresaComStatus } from './derivarProcessos';
import { ORDEM_STATUS } from './ordemStatus';
import { statusEfetivo } from './derivarResumo';
import {
  GRUPO_MACRO,
  GRUPOS_MACRO_ORDENADOS,
  ROTULO_MACRO,
  TOM_DO_GRUPO_MACRO,
  type StatusMacro,
} from './vocabularioMacro';

/**
 * Pílulas-resumo do PopupProcessos (pedido 08/09: "ao clicar nestas pílulas ele
 * filtrar por tipo de status aqui dentro do popup mesmo").
 *
 * Cada pílula mapeia EXATAMENTE um status canônico — é o que permite usá-las como
 * toggles do filtro LOCAL do popup (`filtrarPorStatus` sobre a lista congelada no
 * clique). Evolução deliberada sobre o `popupSummary()` do protótipo: o ✓ somava
 * done+warn numa pílula só; agora warn tem a pílula própria ⚠ (senão o toggle de
 * "concluídas" filtraria dois status de uma vez).
 *
 * As CONTAGENS são sempre TOTAIS da lista recebida (descrevem o nó) — o filtro
 * local muda a lista exibida, nunca os números das pílulas. Limiar do protótipo
 * preservado: pílulas só com ≥2 empresas; só status com contagem > 0 viram pílula
 * (chip clicável só quando visível).
 *
 * A RÉGUA É A MESMA DO NÓ (24/09): as pílulas contam por `statusEfetivo`, que
 * corrige o `st` da dedup com a ocorrência escondida em outro bot do grupo —
 * exatamente o que `contagensDaLista` faz para a meta do nó.
 *
 * Antes cada superfície usava a sua: o nó reclassificava (`err` em outro bot tira
 * do sucesso, pedido de 22/09) e a pílula contava o `st` cru. A MESMA empresa
 * entrava em "falhas" no nó e em "concluídas" na pílula — era o "6 no cabeçalho,
 * 5 na lista".
 *
 * O aviso que estava aqui ("não reclassifique, o toggle abriria o popup VAZIO")
 * apontava um risco REAL: a pílula é um TOGGLE, e o `filtrarPorStatus` recortava
 * por `item.st`. Resolvido na raiz — o filtro do popup passou a recortar por
 * `statusEfetivo` também (`filtrarPorStatusEfetivo`), então a pílula ⚠ acha as
 * linhas que ela conta. Contar por uma régua e filtrar por outra é que abria o
 * popup vazio.
 *
 * Por isso meta do nó e pílulas respondem perguntas diferentes de propósito: a meta
 * diz "existe alerta nesta rota", a pílula diz "quantas linhas estão em cada estado".
 * O alerta escondido continua visível na LINHA, na caixa de mensagem clicável do #723.
 */

export interface PilulaPopup {
  st: StatusEsteira;
  icon: string;
  label: string;
  count: number;
}

/** Ícones do protótipo (✓ concluídas / ✕ falhas / ⚠ alertas / 🚚 andamento / ⏳ fila). */
const ICONES: Record<StatusEsteira, string> = {
  done: '✓',
  err: '✕',
  warn: '⚠',
  run: '🚚',
  // `skip` (24/09): pulada porque nao havia trabalho — nem sucesso nem falha.
  skip: '⊘',
  wait: '⏳',
};

const rotuloDaPilula = (st: StatusEsteira, n: number): string => {
  switch (st) {
    case 'done':
      return `${n} concluída${n > 1 ? 's' : ''}`;
    case 'err':
      return `${n} falha${n > 1 ? 's' : ''}`;
    case 'warn':
      return `${n} alerta${n > 1 ? 's' : ''}`;
    case 'run':
      return `${n} em andamento`;
    // ANTES do `default` (que diz "na fila"): sem este case a pilula afirmaria
    // que a empresa esta' esperando processamento, quando o bot ja' decidiu que
    // nao havia o que fazer nela.
    //
    // "pendente" (25/09) segue o rotulo de `ST_META.skip` — o mesmo estado nao
    // pode ter dois nomes entre o filtro e a pilula.
    case 'skip':
      return `${n} pendente${n > 1 ? 's' : ''}`;
    default:
      return `${n} na fila`;
  }
};

/** Pilulas da MACRO: uma por GRUPO, no vocabulario de `vocabularioMacro`. */
const pilulasMacro = (companies: IEmpresaComStatus[]): PilulaPopup[] | null => {
  const contagem = new Map<StatusMacro, number>();
  companies.forEach(c => {
    const g = GRUPO_MACRO[statusEfetivo(c)];
    contagem.set(g, (contagem.get(g) ?? 0) + 1);
  });
  const pilulas = GRUPOS_MACRO_ORDENADOS.flatMap(g => {
    const n = contagem.get(g) ?? 0;
    if (!n) return [];
    // `st` continua sendo um status CANONICO (o tom do grupo): e' o que o filtro
    // local do popup usa para casar com as linhas, e inventar uma chave nova
    // abriria o popup vazio — a licao do comentario no topo deste arquivo.
    return [
      {
        st: TOM_DO_GRUPO_MACRO[g],
        icon: ICONES[TOM_DO_GRUPO_MACRO[g]],
        label: `${n} ${ROTULO_MACRO[g].toLowerCase()}`,
        count: n,
      },
    ];
  });
  return pilulas.length ? pilulas : null;
};

/**
 * `macro`: agrupa pelo vocabulario da macro antes de contar — "Falha de
 * processamento" soma err + warn, "Empresas a processar" soma run + wait.
 *
 * Contar separado aqui e junto no filtro faria as pilulas do cabecalho
 * discordarem das pilulas do filtro na MESMA tela, que e' o tipo de divergencia
 * que ja' custou caro nesta aba.
 */
export const pilulasDoPopup = (
  companies: IEmpresaComStatus[] | null,
  macro = false,
): PilulaPopup[] | null => {
  if (!companies || companies.length < 2) return null;
  if (macro) return pilulasMacro(companies);
  const contagem = new Map<StatusEsteira, number>();
  companies.forEach(c => {
    const st = statusEfetivo(c);
    contagem.set(st, (contagem.get(st) ?? 0) + 1);
  });
  const pilulas = (Object.keys(ORDEM_STATUS) as StatusEsteira[])
    .sort((a, b) => ORDEM_STATUS[a] - ORDEM_STATUS[b])
    .flatMap(st => {
      const n = contagem.get(st) ?? 0;
      if (!n) return [];
      return [{ st, icon: ICONES[st], label: rotuloDaPilula(st, n), count: n }];
    });
  return pilulas.length ? pilulas : null;
};
